import { expressEventListener, expressQuerySelector, expressQuerySelectorAll, show } from "../common/html";
import { signInWithPopup, signInWithEmailAndPassword, GoogleAuthProvider, AuthProvider, signOut, FacebookAuthProvider } from "firebase/auth";
import { postHtmlRequest, postRequest } from "../utils/fetch";
import { initForm } from "./form";
import { getFullQuerystring, getParameter, getParameters } from "../utils/querystring";
import { getCookie, writeCookie } from "../utils/cookie";
import { createPopup } from "./popup-new";
import { createButton } from "./button";
import { IDisposable } from "../utils/disposable";

enum ExternalLoginProvider {
	Google = 0,
	Facebook = 1
}

export function CreateLoginRegister(onSuccess?: () => void, containerEl?: HTMLElement) {
	const loginRegisterEls = expressQuerySelectorAll<HTMLElement>(containerEl ?? document, '.technical-login-register-container');
	const loginComponents = loginRegisterEls.map(x => {
		const onSuccessIfUndefined = () => {
			if (getFullQuerystring().indexOf('embed') >= 0) {
				// @ts-ignore
				parent.eXpress.loggedOn({ accountId: getCookie('accid'), cartId: getCookie('scid') });
				parent.document.dispatchEvent(new CustomEvent('loggedOn.loginDialog'));
			} else {
				window.location.href = window.location.origin + (decodeURIComponent(getParameter('ReturnUrl') || x.dataset.defaultreturnurl));
			}
		};

		return window.context.identityPlatformEnabled
			? LoginComponent(x, {
				toggleElementClicked: (loginMode: boolean) => { loginComponents.forEach(x => x.toggleShow(x.loginMode === loginMode)); },
				onSuccess: onSuccess || onSuccessIfUndefined
			})
			: ExpressLoginComponent(x, {
				toggleElementClicked: (loginMode: boolean) => { loginComponents.forEach(x => x.toggleShow(x.loginMode === loginMode)); },
				onSuccess: onSuccess
			});
	});
}
export interface LoginComponentDeps {
	toggleElementClicked: (loginMode) => void;
	onSuccess: () => void;
}

interface IExpressLoginRequest {
	Email: string;
	Password: string;
	RememberMe: string;
	RedirectUrl: string;
}
interface IExpressLoginResult {
	redirectUrl: string;
	isNotAuthenticated: boolean;
	errorMsg: string;
	blockingErrorMsg: string;
	blockingErrorLabel: string;
	accountId: string;
	cartId: string;
	isSoftDeleted: boolean;
}
interface ILoginSubmitResult {
	readonly redirectUrl?: string;
	readonly isNotAuthenticated?: boolean;
	readonly errorMsg?: string;
	readonly blockingErrorMsg?: string;
	readonly accountId?: string;
	readonly cartId?: string;
	readonly isSoftDeleted?: boolean;
}
interface IExpressRegisterRequest {
	Email: string;
	Password: string;
	ConfirmedPassword: string;
	PersonalMessage: string;
	NewsLetter: string;
	AllowProductReviews: string;
	Conditions: string;
	RedirectUrl: string;
}
interface IExpressRegisterResponse {
	success: boolean;
	accountInOtherDomain: string;
	redirectUrl: string;
	accountId: string;
	cartId: string;
	switchToLogin: boolean;
	message: string;
	email: string;
	accountBlocked: string;
	isSoftDeleted: true;
}
interface ILoginUserAtSmartPhotoResult {
	readonly accountData?: IAccountData;
	readonly loginResult: ILoginResult;
	readonly accountType: string;
	readonly cartView?: string;
	readonly msgView?: string;
	readonly redirectUrl?: string;
	readonly success?: boolean;
}
interface IAccountData {
	readonly DeliveryAddressData: IAddress;
	readonly BillingAddressData: IAddress;
	readonly Email: string;
	readonly FirstName: string;
	readonly HideNewsletter: boolean;
	readonly LastName: string;
	readonly Message?: string;
	readonly SendNewsletter?: boolean;
	readonly ShowProfileForm?: boolean;
	readonly ShowProfileFormExtraText?: boolean;
}
export interface IAddress {
	HouseNumber?: string;
	Id?: string;
	IsDefault?: boolean;
	City?: string;
	RegionCountryISO?: string;
	readonly Country?: string;
	FirstName?: string;
	LastName?: string;
	CompanyName?: string;
	Line1?: string;
	Line2?: string;
	Line3?: string;
	Line4?: string;
	ZipCode?: string;
	VatNumber?: string;
	Street?: string;
	BoxNumber?: string;
	ExtraAddressInformation?: string;
}
interface ILoginResult {
	AccountId: string;
	tempAccountId: string;
	readonly PersonId: string;
	readonly cartId: string;
	readonly Email: string;
	readonly redirectUrl: string;
	readonly success: string;
	readonly PopUpCase: string;
}
export function ExpressLoginComponent(containerEl: HTMLElement, deps: LoginComponentDeps) {
	const { toggleElementClicked, onSuccess } = deps;
	const loginMode = containerEl.dataset.mode === 'register';
	const form = expressQuerySelector<HTMLFormElement>(containerEl, '.technical-login-register-form', true);
	const externalLoginSubmitButtons = expressQuerySelectorAll<HTMLButtonElement>(containerEl, '.technical-external-login');
	const toggleEl = expressQuerySelector<HTMLElement>(containerEl, '.technical-label a'); // link in label
	const forgetPassword = expressQuerySelector<HTMLElement>(containerEl, '.technical-forgot-password', false);

	const login = (data): Promise<void> => {
		return postRequest<IExpressLoginRequest, IExpressLoginResult>('/ajaxxhr/Login/NormalLogin',
			{
				Email: data['email'],
				Password: data['password'],
				RememberMe: data['rememberme'],
				RedirectUrl: decodeURIComponent(getParameter('ReturnUrl'))
			},
			{ headers: { 'Content-type': 'application/json; charset=utf-8' } }).then(res => {
				if (res.isNotAuthenticated && !res.errorMsg && !res.blockingErrorMsg) res.errorMsg = 'Something went wrong';
				if (res.errorMsg) {
					// show error in page
					formComponent.inputs['password'].setError(res.errorMsg);
				}

				if (res.isSoftDeleted) {
					const formData = JSON.stringify({
						Email: data['email'],
						Password: data['password'],
						RememberMe: data['rememberme'],
						RedirectUrl: decodeURIComponent(getParameter('ReturnUrl'))
					});

					// show popup
					createIsSoftDeletedPopup("ConfirmDialog", "isSoftDeleted", formData);
				}

				if (res.blockingErrorLabel) {
					if (formComponent) {
						formComponent.inputs['password'].clear();
						formComponent.inputs['password'].setValue('');
					}

					// show popup
					createPopupWithBodyLabel("GenericPopup", "blockingErrorMsg", res.blockingErrorLabel, "m-popup--content-width");
				}

				if (!res.isNotAuthenticated && res.redirectUrl && res.redirectUrl.length > 0) {
					const dataLayer = window.dataLayer || [];
					dataLayer.push({ 'event': 'login' });
					const iframe = expressQuerySelector<HTMLIFrameElement>(document, '#dataLayerIframe', false);
					if (iframe && iframe.contentWindow && (iframe.contentWindow as any).pushDataLayer) {
						(iframe.contentWindow as any).pushDataLayer({ 'event': 'login' });
					}

					if (!getParameter('embed')) {
						// redirect
						return onSuccess ? onSuccess() : window.location.href = res.redirectUrl;
					} else if (typeof parent != undefined) {
						// @ts-ignore
						parent.eXpress && parent.eXpress.loggedOn && parent.eXpress.loggedOn({ accountId: res.accountId, cartId: res.cartId });
						parent.document.dispatchEvent(new CustomEvent('loggedOn.loginDialog'));
					}
				}
			})
			.then(_ => postHtmlRequest("/apicore/log/LogInformation", { InformationText: 'user logged in with express', Error: false }, { headers: { 'Content-type': 'application/json; charset=utf-8' } }).then(res => { return; }))
			.catch(err => { return; });
	};
	const createIsSoftDeletedPopup = (view: string, dialogId: string, data: any) => {
		const undeleteAsync = (formData: any) => {
			return postRequest<any, ILoginSubmitResult>('/ajaxxhr/Login/UndeleteLogin', formData, { headers: { 'Content-type': 'application/json; charset=utf-8' } })
				.then(result => {
					if (!result.isNotAuthenticated && !result.blockingErrorMsg && result.redirectUrl) {
						window.history.replaceState({}, document.title, result.redirectUrl);
						window.location.replace(result.redirectUrl + "?success=true");
					}
				});
		};

		createPopup({
			view: view,
			dialogId: dialogId,
			data: {
				Question: 'Label.UserLogin.LoginPage.DeletedAccount.Description',
				Title: 'Label.UserLogin.LoginPage.DeletedAccount.Title',
				OkMnemonic: 'Label.UserLogin.LoginPage.DeletedAccount.Continue',
				CancelMnemonic: 'Label.UserLogin.LoginPage.DeletedAccount.Cancel'
			}
		})
			.then(popup => {
				const dialog = expressQuerySelector<HTMLElement>(document, '#' + dialogId, true);
				const okButton = expressQuerySelector<HTMLElement>(dialog, '.technical-remove-confirmation-yes-button', true);
				const cancelButton = expressQuerySelector<HTMLElement>(dialog, '.technical-remove-confirmation-no-button', true);

				okButton.addEventListener("click", function () { popup.close(); undeleteAsync(data); });
				cancelButton.addEventListener("click", function () { popup.close(); });
			});
	};
	const createPopupWithBodyLabel = (view: string, dialogId: string, bodyLabel: string, extraPopupCss?: string) => {
		let replacements: string[] = [];
		if (bodyLabel.indexOf('|') > 0) {
			const bodyArr = bodyLabel.split('|');
			bodyLabel = bodyArr[0];
			replacements = bodyArr[1].split(',');
		}

		createPopup({
			view: view,
			dialogId: dialogId,
			data: {
				BodyLabel: bodyLabel,
				BodyReplacements: replacements,
				OkButtonLabel: 'Label.CloseButtonText',
				ExtraPopupCss: `p-default ${extraPopupCss}`
			}
		}).then(res => {
			const dialog = expressQuerySelector<HTMLElement>(document, '#' + dialogId, true);
			const okButton = expressQuerySelector<HTMLElement>(dialog, '.technical-general-popup-ok-button', true);

			okButton.addEventListener("click", function () { res.close(); });

			res.openAsync();
		});
	};
	const externalLogin = (containerEl: HTMLButtonElement, provider: ExternalLoginProvider) => {
		switch (provider) {
			case ExternalLoginProvider.Google: return createGoogleLogin(containerEl, {
				appId: containerEl.getAttribute("app_id") || '',
				loginCallback: (uid, accessToken, expire) => loginUserAtSmartPhoto(uid, accessToken, expire, "googlephotos", "", false, !!containerEl.getAttribute("data-orderflow"), false)
			});
			case ExternalLoginProvider.Facebook: return createFacebookLoginButton(containerEl, { loginUserAtSmartPhoto: loginUserAtSmartPhoto });
		}
	};
	const loginUserAtSmartPhoto = (uid: string, accessToken: string, expire: number, mediaStorePartnerType: string, newEmail: string, skipLogin: boolean, onOrderFlow: boolean, async: boolean) => {
		return postRequest<{ userId: string, accessToken: string, expire: number, mediastorePartnerType: string, newEmail: string, skipLogin: boolean, onOrderFlow: boolean, async: boolean }, ILoginResult | ILoginUserAtSmartPhotoResult>
			('/ajaxxhr/Login/LoginUserAtSmartPhoto', {
				userId: uid,
				accessToken: accessToken,
				expire: expire,
				mediastorePartnerType: mediaStorePartnerType,
				newEmail: newEmail,
				skipLogin: skipLogin,
				onOrderFlow: onOrderFlow,
				async: async
			}, { headers: { 'Content-type': 'application/json; charset=utf-8' } }
			).then(res => handleExternalLoginResult(res, mediaStorePartnerType, async, undefined, undefined, undefined, undefined, uid, accessToken));
	};
	const handleExternalLoginResult = (
		response: ILoginResult | ILoginUserAtSmartPhotoResult,
		mediaStorePartnerType: string,
		async: boolean,
		accountId = "",
		tempAccountId = "",
		showConfirmationBeforeRedirect = false,
		loginNotCorrect = false,
		uid = "",
		accessToken = "") => {
		let loginUserAtSmartPhotoResponse: ILoginUserAtSmartPhotoResult | undefined = undefined;
		let loginResult: ILoginResult | undefined = undefined;

		if (response && (response as any).loginResult)
			loginUserAtSmartPhotoResponse = response as ILoginUserAtSmartPhotoResult;
		else
			loginResult = response as ILoginResult;

		let asyncSuccess = true;
		if (async) {
			if (loginResult && loginResult.success == 'true') {
				asyncSuccess = false;
			}
		}
		if (async && asyncSuccess && loginUserAtSmartPhotoResponse) {
			loginResult = loginUserAtSmartPhotoResponse.loginResult as ILoginResult;
		}
		if (asyncSuccess && loginResult && loginResult.success == 'true') {
			if (loginResult.redirectUrl) {
				if (!getParameter('embed')) {
					const url = loginResult.redirectUrl.substring(loginResult.redirectUrl.indexOf('\'') + 1, loginResult.redirectUrl.lastIndexOf('\'')); // little hack for wrong url content .net core site => "'/nl/...'"  => '/nl/...'
					onSuccess ? onSuccess() : window.location.href = url ? window.location.origin + url : window.location.origin;
				} else if (typeof parent != undefined) {
					// @ts-ignore
					parent.eXpress && parent.eXpress.loggedOn && parent.eXpress.loggedOn({ accountId: loginResult.AccountId, cartId: loginResult.CartId });
					parent.document.dispatchEvent(new CustomEvent('loggedOn.loginDialog'));
				}
			}
		} else if (loginResult) {
			// We need to correct the result for TEMP accounts (only Popupcase and success are returned)
			if (!loginResult.AccountId && accountId) loginResult.AccountId = accountId;
			if (!loginResult.tempAccountId && tempAccountId) loginResult.tempAccountId = tempAccountId;
			handleLoginFailed(loginResult, mediaStorePartnerType, loginNotCorrect, async, mediaStorePartnerType, uid, accessToken);
		}
	};
	const handleLoginFailed = (
		loginResult: ILoginResult,
		mediaStorePartnerType: string,
		loginNotCorrect: boolean,
		async: boolean,
		mediaStorePartnerLabel: string,
		uid = "",
		accessToken = "",
		expire = 0,
		skipLogin = false) => {

		const alreadyTriedNewEmail = false;
		let accountInOtherDomainCalled = 0;


		switch (loginResult.PopUpCase) {
			case "AccountBlocked":
				createPopup({
					dialogId: 'AccountBlocked',
					view: 'GenericPopup',
					data: {
						BodyLabel: 'Label.UserLogin.AccountBlocked',
						OkButtonLabel: 'Label.OkButtonText',
						ExtraPopupCss: `p-default`
					}
				}).then(res => {
					const okButton = expressQuerySelector<HTMLElement>(res.popup, '.technical-general-popup-ok-button', true);
					okButton.addEventListener("click", function () { res.close(); });
					res.openAsync();
				});
				break;
			case "AskPassword":
				createPopup({
					dialogId: 'AskPassword',
					view: 'AskPasswordDialog',
					data: {
						LoginNotCorrect: loginNotCorrect,
						AccountId: loginResult.AccountId,
						AlreadyTriedEmail: alreadyTriedNewEmail,
						MediaStorePartnerLabel: mediaStorePartnerLabel
					},
					forceRefresh: true
				}).then(res => {
					const formEl = expressQuerySelector<HTMLFormElement>(res.popup, ".technical-ask-password-form", true);
					const sendPassWordLinkEl = expressQuerySelector<HTMLAnchorElement>(res.popup, ".technical-send-password-link", true);
					const passwordSendEl = expressQuerySelector<HTMLElement>(res.popup, ".technical-password-send", true);

					const confirmAskPassword = (password: string) => {
						return postRequest<{ accountId: string, tempAccountId: string, mediastorePartnerType: string, password: string, async: boolean }, ILoginResult | ILoginUserAtSmartPhotoResult>
							('/ajaxxhr/Login/SingleSignOnConfirmPassword', {
								accountId: loginResult.AccountId,
								tempAccountId: loginResult.tempAccountId,
								mediastorePartnerType: mediaStorePartnerType,
								password: encodeURIComponent(password),
								async: async
							}, { headers: { 'Content-type': 'application/json; charset=utf-8' } })
							.then(singleSignOnResponse => {
								res.close();
								handleExternalLoginResult(singleSignOnResponse, mediaStorePartnerType, async, loginResult.AccountId, loginResult.tempAccountId, true, true);
							});
					};

					const sendPassword = (email: string) => {
						return postRequest('/ajaxxhr/Login/SingleSignOnSendPassword', {
							email: email,
							url: encodeURIComponent(window.location.href)
						}, { headers: { 'Content-type': 'application/json; charset=utf-8' } });
					};

					initForm(formEl, {
						onSubmitForm: (formData) => confirmAskPassword(formData["password"]),
						submitOnEnter: true
					});

					sendPassWordLinkEl.addEventListener('click', e => {
						e.preventDefault();

						show(passwordSendEl);
						const email = sendPassWordLinkEl.dataset.email;
						email && sendPassword(email);
					});

					res.openAsync();
				});
				break;
			case "AlreadyLoggedIn":
				createPopup({
					dialogId: 'AlreadyLoggedIn',
					view: 'GenericPopup',
					data: {
						BodyLabel: 'Label.UserLogin.LoginPage.AlreadyLoggedIn',
						OkButtonLabel: 'Label.OkButtonText',
						ExtraPopupCss: `p-default`
					}
				}).then(res => {
					const okButton = expressQuerySelector<HTMLElement>(res.popup, '.technical-general-popup-ok-button', true);
					okButton.addEventListener("click", function () { res.close(); });
					res.openAsync();
				});
				break;
			case "FatalErrorMessage":
				createPopup({
					dialogId: `${mediaStorePartnerLabel}_FatalErrorMessage`,
					view: 'GenericPopup',
					data: {
						BodyLabel: 'Label.SingleSignOn.FatalError',
						OkButtonLabel: 'Label.OkButtonText',
						ExtraPopupCss: `p-default`,
						BodyReplacements: [mediaStorePartnerLabel]
					}
				}).then(res => {
					const okButton = expressQuerySelector<HTMLElement>(res.popup, '.technical-general-popup-ok-button', true);
					okButton.addEventListener("click", function () { res.close(); });
					res.openAsync();
				});
				break;
			// case "ConfirmEmail": break; // WEB-3835 => no longer used
			// case "OtherEmail": break;// WEB-3835 => no longer used
			case "FbIdAlreadyConnected":
				createPopup({
					dialogId: `${mediaStorePartnerLabel}_AlreadyConnected`,
					view: 'GenericPopup',
					data: {
						BodyLabel: `Label.Profile.SingleSignOn.${mediaStorePartnerLabel}.Error.AlreadyConnected`,
						OkButtonLabel: 'Label.OkButtonText',
						ExtraPopupCss: `p-default`,
						BodyReplacements: [mediaStorePartnerLabel]
					}
				}).then(res => {
					const okButton = expressQuerySelector<HTMLElement>(res.popup, '.technical-general-popup-ok-button', true);
					okButton.addEventListener("click", function () { res.close(); });
					res.openAsync();
				});
				break;
			case "AccountInOtherDomain":
				createPopup({
					dialogId: 'AccountInOtherDomain',
					view: 'AccountInOtherDomainDialog',
					forceRefresh: true,
					data: {
						accountId: loginResult.AccountId,
						alreadyTriedEmail: accountInOtherDomainCalled > 0
					}
				}).then(res => {
					initForm(
						expressQuerySelector(res.popup, '.technical-account-in-other-domain-form'),
						{
							onSubmitForm: formData => {
								res.close();
								return loginUserAtSmartPhoto(uid, accessToken, expire, mediaStorePartnerType, formData["email"], skipLogin, false, async);
							},
							submitOnEnter: true
						});

					res.openAsync();
				});
				accountInOtherDomainCalled++;
				break;
			case "ShowNewsletter":
				createPopup({
					dialogId: 'ShowNewsletter',
					view: 'ShowNewsletterDialog',
					data: {
					}
				}).then(res => {
					const redirectUrl = loginResult.redirectUrl;
					const confirmShowNewsletter = (isChecked: boolean): Promise<void> => {
						return postRequest('/ajaxxhr/Login/UpdateNewsletterCheckBox', {
							personId: loginResult.PersonId,
							isChecked: isChecked
						}, { headers: { 'Content-type': 'application/json; charset=utf-8' } }).then(updatedResult => {
							res.close();
							redirectUrl &&
								window.setTimeout(function () {
									window.location.href = redirectUrl.replace(/'/g, '');
								}, 0);
						});
					};

					initForm(
						expressQuerySelector(res.popup, '.technical-show-newsletter-form'),
						{
							onSubmitForm: formData => confirmShowNewsletter(formData["newslettersso"] === 'true'),
							submitOnEnter: true
						}
					);
					res.openAsync();
				});
				break;
			default:
				createPopup({
					dialogId: 'generalError',
					view: 'GenericPopup',
					data: {
						BodyLabel: 'Label.Profile.SingleSignOn.Facebook.GeneralError',
						OkButtonLabel: 'Label.OkButtonText',
						ExtraPopupCss: `p-default`,
						BodyReplacements: [mediaStorePartnerLabel]
					}
				}).then(res => {
					const okButton = expressQuerySelector<HTMLElement>(res.popup, '.technical-general-popup-ok-button', true);
					okButton.addEventListener("click", function () { res.close(); });
					res.openAsync();
				});
				break;
		}
	};
	const register = (data): Promise<void> => {
		return postRequest<IExpressRegisterRequest, IExpressRegisterResponse>('/ajaxxhr/Login/Registration', {
			Email: data['email'],
			Password: data['password'],
			ConfirmedPassword: data['password'],
			PersonalMessage: data['personalmessage'] || 'false',
			NewsLetter: data['newsletter'],
			AllowProductReviews: data['allowproductreviews'] || 'false',
			Conditions: data['termsandconditions'],
			RedirectUrl: decodeURIComponent(getParameter('ReturnUrl') || '')
		},
			{ headers: { 'Content-type': 'application/json; charset=utf-8' } }).then(res => {
				if (res.switchToLogin && res.success) toggleElementClicked(loginMode);

				if (res.success) {
					const datalayer = window.dataLayer || [];
					datalayer.push({ 'event': 'fullAccount' });
					const iframe = expressQuerySelector<HTMLIFrameElement>(document, '#dataLayerIframe', false);
					if (iframe && iframe.contentWindow && (iframe.contentWindow as any).pushDataLayer)
						(iframe.contentWindow as any).pushDataLayer({ 'event': 'fullAccount' });

					if (!getParameter('embed')) {
						// redirect
						window.location.href = res.redirectUrl ? res.redirectUrl : window.location.origin;
					} else if (typeof parent != undefined) {
						// @ts-ignore
						parent.eXpress && parent.eXpress.loggedOn && parent.eXpress.loggedOn({ accountId: res.accountId, cartId: res.cartId });
						parent.document.dispatchEvent(new CustomEvent('loggedOn.loginDialog'));
					}
				}

				if (res.message) {
					// show message in page
					formComponent.inputs['password'].setError(res.message);
				}

				if (res.isSoftDeleted) {
					const formData = JSON.stringify({
						Email: data['email'],
						Password: data['password'],
						RememberMe: false,
						RedirectUrl: decodeURIComponent(getParameter('ReturnUrl'))
					});

					// show popup
					createIsSoftDeletedPopup("ConfirmDialog", "isSoftDeleted", formData);
				}

				if (res.accountInOtherDomain) {
					// show popup
					createPopupWithBodyLabel("GenericPopup", "accountInOtherDomain", res.accountInOtherDomain);
				}

				if (res.accountBlocked) {
					// show popup
					createPopupWithBodyLabel("GenericPopup", "accountBlocked", res.accountBlocked, "m-popup--content-width");
				}
			})
			.then(_ => postHtmlRequest("/apicore/log/LogInformation", { InformationText: 'user registerd with express', Error: false }, { headers: { 'Content-type': 'application/json; charset=utf-8' } }).then(_ => { return; }))
			.catch(err => { return; });
	};

	const formComponent = initForm(form, {
		onSubmitForm: !loginMode ? login : register,
		alternativeButton: expressQuerySelector<HTMLButtonElement>(containerEl, '.technical-submit-button')
	});

	externalLoginSubmitButtons.forEach(x => externalLogin(x, ExternalLoginProvider[x.dataset.provider]));
	toggleEl.addEventListener('click', e => { e.preventDefault(); toggleElementClicked(loginMode); });
	forgetPassword && forgetPassword.addEventListener('click', e => {
		e.preventDefault(); createPopup({
			dialogId: 'forgotPassword',
			view: 'ForgotPasswordDialog',
			forceRefresh: true,
			closeOnClickOutsidePopup: true
		}).then(res => {
			const formEl = expressQuerySelector<HTMLFormElement>(res.popup, '.technical-forgot-password-form');
			initForm(formEl, {
				onSubmitForm: (data: { [inputName: string]: string }) => postRequest('/apicore/authentication/forgotpassword', { email: data['email'] }, { headers: { 'Content-type': 'application/json; charset=utf-8' } }).then(_ => res.close()),
				submitOnEnter: true
			});
			res.openAsync();
		});
	});

	return {
		loginMode,
		toggleShow: (force?: boolean) => {
			containerEl.classList.toggle('u-hide', force);
			!force && getFullQuerystring().includes("embed") && containerEl.scrollIntoView({ inline: 'start', block: 'start', behavior: 'auto' });
		}
	};
}

export function createGoogleLogin(buttonEL: HTMLElement, deps: { appId: string, loginCallback: (uid: string, accessToken: string, expire: number) => void, onInit?: () => void }) {
	const { appId, loginCallback, onInit } = deps;
	const scope = "profile email https://www.googleapis.com/auth/photoslibrary";
	const errorsToIgnore = ["popup_closed_by_user", "access_denied"];

	const initClient = () => {
		gapi.auth2.init({
			client_id: appId,
			fetch_basic_profile: true,
			scope
		}).then(auth => {
			onInit && onInit();
			auth.attachClickHandler(
				buttonEL,
				{
					scope,
					prompt: "select_account consent"
				},
				(user) => {
					const authResponse = user.getAuthResponse(true);
					loginCallback(
						user.getBasicProfile().getId(),
						authResponse.access_token,
						authResponse.expires_in
					);
				},
				(reason) => { // sign in failed
					const error = reason["error"];
					if (error && !errorsToIgnore.some((value) => value === error)) {
						throw new Error(error);
					}
				}
			);
		}, (reason) => { // init client failed
			throw new Error(`${reason.error} : ${reason.details}`);
		});
	};

	// load google script async
	const scriptEl = document.createElement('script');
	scriptEl.type = 'text/javascript';
	scriptEl.async = true;
	scriptEl.defer = true;
	scriptEl.onload = () => gapi.load('client:auth2', initClient);
	scriptEl.src = 'https://apis.google.com/js/client:platform.js';
	const firstScriptEl = document.getElementsByTagName('script')[0];
	firstScriptEl.parentElement!.insertBefore(scriptEl, firstScriptEl);
}
export interface ICreateFacebookLoginButtonDependencies {
	readonly loginUserAtSmartPhoto: (uid: string, accessToken: string, expire: number, mediaStorePartnerType: string, newEmail: string, skipLogin: boolean, onOrderFlow: boolean, async: boolean) => void;
}
export function createFacebookLoginButton(facebookLoginButtonEl: HTMLButtonElement, deps: ICreateFacebookLoginButtonDependencies) {
	const { loginUserAtSmartPhoto } = deps;
	let disposableEvent: IDisposable;
	let isFacebookInitialized = false;
	let facebookLoggedIn = false;
	let onOrderFlow = false;

	const scopePhotos = 'user_photos';
	let scopeExtra = '';
	const scopeEmail = 'email';
	let facebookScope = scopePhotos + ',' + scopeEmail;
	const mediaStorePartnerType = 'facebook';

	const facebookButton = createButton(facebookLoginButtonEl);

	const onClick = (ev: Event) => {
		if (ev.preventDefault) {
			ev.preventDefault();
		}
		ev.stopPropagation();
		facebookButton.disable();
		const perms = facebookLoginButtonEl.dataset.perms ? facebookLoginButtonEl.dataset.perms : '';
		loginToFacebook(false, perms, true);
	};

	const init = () => {
		if (typeof (FB) === "undefined")
			return;

		if (!isFacebookInitialized) {
			const appId = facebookLoginButtonEl.dataset.appid ? facebookLoginButtonEl.dataset.appid : '';
			initFacebook(appId);
			show(facebookLoginButtonEl);
		}

		disposableEvent = expressEventListener(facebookLoginButtonEl, 'click', onClick);

		FB.getLoginStatus(function (loginresponse) {
			if (loginresponse.status === 'connected') {
				const uid = loginresponse.authResponse.userID;
				/* make the API call */
				FB.api(
					"/" + uid + "/permissions",
					function (response) {
						facebookLoggedIn = false;
						if (response && !response.error) {
							if (response.data && response.data.length > 0 && response.data[0] !== null) {
								if (response.data[0].email === 1 && response.data[0].user_photos === 1) {
									facebookLoggedIn = true;
								}
							}
						}
					}
				);
			} else if (loginresponse.status === 'not_authorized') {
				// the user is logged in to Facebook, but has not authenticated your app
				facebookLoggedIn = false;
			} else {
				// the user isn't logged in to Facebook.
				facebookLoggedIn = false;
			}
			facebookButton.enable();
		}, true);
	};

	const initFacebook = (appId: string) => {
		FB.init({ appId: appId, status: false, cookie: true, version: 'v6.0' }); //status: false check the login status upon init -> we will do this with getLoginStatus
		isFacebookInitialized = true;
	};

	const fBLoginWithPermissions = (skipLogin, async) => {

		//showLoadingDialog();
		// fbdialogTimer = window.setTimeout(HideLoadingDialog, 3000);
		FB.login(function (loginresponse) {
			// clearTimeout(fbdialogTimer);
			// connected
			if (loginresponse.authResponse) {
				facebookLoggedIn = true;
				// check if all permissions are granted (result is also 'connected' when permissions are partialy granted)
				const uid = loginresponse.authResponse.userID;
				//showLoadingDialog();
				FB.api(
					"/" + uid + "/permissions",
					function (response) {
						facebookLoggedIn = false;
						if (response && !response.error) {
							if (response.data && response.data.length > 0 && checkFacebookPermissions(response.data)) {
								// user granted extended permissions
								const accessToken = loginresponse.authResponse.accessToken;
								const expireTime = loginresponse.authResponse.expiresIn;
								loginUserAtSmartPhoto(uid, accessToken, expireTime, mediaStorePartnerType, '', skipLogin, onOrderFlow, async);
							} else {
								// user cancelled extended permissions	
							}
							//hideLoadingDialog();
						}
					}
				);
			}

			facebookButton.enable();
			//hideLoadingDialog();
		}, { scope: facebookScope });
	};

	const loginToFacebook = (skipLogin, extraPermissions, async) => {
		if (typeof (FB) === "undefined")
			return;

		if (skipLogin === null) skipLogin = false;
		onOrderFlow = async;
		scopeExtra = extraPermissions;
		if (scopeExtra && scopeExtra !== '')
			facebookScope = facebookScope + ',' + scopeExtra;

		if (facebookLoggedIn) {
			// are we still connected facebook?
			//showLoadingDialog();
			FB.getLoginStatus(function (loginresponse) {
				if (loginresponse.status === 'connected') {
					// the user is logged in and has authenticated your app, and response.authResponse supplies
					// the user's ID, a valid access token, a signed request, and the time the access token and signed request each expire	
					const uid = loginresponse.authResponse.userID;
					//showLoadingDialog();
					FB.api(
						"/" + uid + "/permissions",
						function (response) {
							facebookLoggedIn = false;
							if (response && !response.error) {
								// Check if permissions are granted. 
								// NOTE: We do not currently check the extraPermissions 
								// But they are currently always empty on our website.
								if (response.data && response.data.length > 0 && checkFacebookPermissions(response.data)) {
									const accessToken = loginresponse.authResponse.accessToken;
									const expireTime = loginresponse.authResponse.expiresIn;
									loginUserAtSmartPhoto(uid, accessToken, expireTime, mediaStorePartnerType, '', skipLogin, onOrderFlow, async);
								} else {
									fBLoginWithPermissions(skipLogin, async);
								}
								//hideLoadingDialog();
							}
						}
					);
				} else if (loginresponse.status === 'not_authorized') {
					// the user is logged in to Facebook, but has not authenticated your app
					fBLoginWithPermissions(skipLogin, async);
				} else {
					// the user isn't logged in to Facebook.
					fBLoginWithPermissions(skipLogin, async);
				}
				//hideLoadingDialog();
			}, true);
		} else {
			fBLoginWithPermissions(skipLogin, async);
		}
	};

	const checkFacebookPermissions = (data) => {

		// todo: this is the old validation, can be removed after the fb apps are swithched to Oauth2.0
		if (data[0] !== null && typeof data[0].email !== "undefined" && typeof data[0].user_photos !== "undefined") {
			return data[0] !== null && data[0].email === 1 && data[0].user_photos === 1;
		}

		const email: any = (data as any[]).filter(e => e.permission === "email");
		const photos: any = (data as any[]).filter(e => e.permission === "user_photos");

		const emailGranted = email != null && email[0].status === "granted";
		const photosGranted = photos != null && photos[0].status === "granted";

		return emailGranted === photosGranted === true;
	};

	const deInit = () => disposableEvent && disposableEvent.dispose();

	init();

	return {
		dispose: deInit,
	};
}

export function LoginComponent(containerEl: HTMLElement, deps: LoginComponentDeps): { loginMode: boolean, toggleShow: (force?: boolean) => void } {
	const { toggleElementClicked, onSuccess } = deps;
	const loginMode = containerEl.dataset.mode === 'register';
	const form = expressQuerySelector<HTMLFormElement>(containerEl, '.technical-login-register-form', true);
	const submitButton = expressQuerySelector<HTMLButtonElement>(containerEl, '.technical-submit-button');
	const externalLoginSubmitButtons = expressQuerySelectorAll<HTMLButtonElement>(containerEl, '.technical-external-login');
	const toggleEl = expressQuerySelector<HTMLElement>(containerEl, '.technical-label a'); // link in label
	const forgetPassword = expressQuerySelector<HTMLElement>(containerEl, '.technical-forgot-password', false);

	const login = (data): Promise<void> => {
		return emailAndPassword(data['email'], data['password']);
	};
	const register = (data): Promise<void> => {
		const email = data['email'];
		const password = data['password'];
		return postRequest(`/apicore/authentication/register`,
			{
				email: email,
				password: password,
				action: 'register',
				Newsletter: data['newsletter'] === 'true',
				PersonalMessage: data['personalmessage'] === 'true',
				AllowProductReviews: data['allowproductreviews'] === 'true',
			},
			{ headers: new Headers({ 'Content-type': 'application/json; charset=utf-8' }) })
			.then((data: { success: boolean, status?: number, error: string }) => {
				if (!data.success) {
					switch (data.status) {
						case 4: // WeakPassword
							formComponent.inputs['password'].setError(data.error);
							break;
					}
					// errorEl.innerHTML = data.error;
					return;
				}

				emailAndPassword(email, password);
			});
	};
	const emailAndPassword = async (email: string, password: string): Promise<void> => {
		return signInWithEmailAndPassword(window.Authentication, email, password)
			.then(res => {
				// validate user account
				return postRequest<{ email, uuid }, { success, error }>(`/apicore/authentication/validatelogin`,
					{ email: email, uuid: res.user.uid },
					{ headers: new Headers({ 'Content-type': 'application/json; charset=utf-8' }) },
					false)
					.then((validated: { success: boolean, error: string }) => {
						// user should not be logged in
						if (!validated.success) {
							signOut(window.Authentication);
							// errorEl.innerHTML = validated.error;
							return;
						}

						return res.user.getIdToken(false).then(token => {
							const authInfo = JSON.stringify({
								refreshToken: res.user.refreshToken,
								jwt: token,
								uid: res.user.uid
							});
							writeCookie('gAuthInfo', '', -1);
							writeCookie('gAuthInfo', authInfo, 365);
						});
					});
			})
			.then(_ => postHtmlRequest("/apicore/log/LogInformation", { InformationText: 'user logged in with google identity', Error: false }, { headers: { 'Content-type': 'application/json; charset=utf-8' } }))
			.then(_ => onSuccess())
			.catch(err => { return; });
	};
	const externalProviderLoginWithPopup = (provider: AuthProvider): Promise<void> => {
		return signInWithPopup(window.Authentication, provider)
			.then(res => {
				// validate user account
				return postRequest(`/apicore/authentication/validatelogin`,
					{ email: res.user.email, uuid: res.user.uid },
					{ headers: new Headers({ 'Content-type': 'application/json; charset=utf-8' }) },
					false)
					.then((validated: { success: boolean, error: string }) => {
						// user should not be logged in
						if (!validated.success) {
							signOut(window.Authentication);
							// errorEl.innerHTML = validated.error;
							return;
						}
						return res.user.getIdToken(false).then(token => {
							const authInfo = JSON.stringify({
								refreshToken: res.user.refreshToken,
								jwt: token,
								uid: res.user.uid
							});
							writeCookie('gAuthInfo', '', -1);
							writeCookie('gAuthInfo', authInfo, 365);
						});
					});
			})
			.then(_ => postHtmlRequest("/apicore/log/LogInformation", { InformationText: 'user logged in with google identity with a external partner', Error: false }, { headers: { 'Content-type': 'application/json; charset=utf-8' } }))
			.then(_ => onSuccess())
			.catch(err => { return; });
	};
	const externalLogin = (provider: ExternalLoginProvider) => {
		switch (provider) {
			case ExternalLoginProvider.Google: return externalProviderLoginWithPopup(new GoogleAuthProvider());
			case ExternalLoginProvider.Facebook: return externalProviderLoginWithPopup(new FacebookAuthProvider());
		}
	};

	const formComponent = initForm(form, {
		onSubmitForm: !loginMode ? login : register,
		alternativeButton: submitButton
	});

	toggleEl.addEventListener('click', e => { e.preventDefault(); toggleElementClicked(loginMode); });
	forgetPassword && forgetPassword.addEventListener('click', e => {
		e.preventDefault(); createPopup({
			dialogId: 'forgotPassword',
			view: 'ForgotPasswordDialog',
			forceRefresh: true,
			closeOnClickOutsidePopup: true
		}).then(res => {
			if (!res.popup) return;
			const formEl = expressQuerySelector<HTMLFormElement>(res.popup, '.technical-forgot-password-form');
			initForm(formEl, {
				onSubmitForm: (data: { [inputName: string]: string }) => postRequest('/apicore/authentication/forgotpassword', { email: data['email'] }, { headers: { 'Content-type': 'application/json; charset=utf-8' } }).then(_ => res.close()),
				submitOnEnter: true
			});
			res.openAsync();
		});
	});
	externalLoginSubmitButtons.forEach(x => x.addEventListener('click', _ => externalLogin(ExternalLoginProvider[x.dataset.provider])));

	return {
		loginMode,
		toggleShow: (force?: boolean) => {
			containerEl.classList.toggle('u-hide', force);
			!force && getFullQuerystring().includes("embed") && containerEl.scrollIntoView({ inline: 'start', block: 'start', behavior: 'auto' });
		}
	};
}
